import React from 'react';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import { twoDecimalNumber } from '../../utils/numbers';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ArrowBack } from '@mui/icons-material';
import Image from '../../assets/Image';
// import WebinarbannerDesktop from '../../assets/CommissionTableBanner.png';
import { useProfile } from '../../context/ProfileContext';

interface CommissionTableProps {}

// Add these objects at the top of the file, outside the component
const regularDealsData = [{ rangeLower: 0, rangeUpper: 0, commissionPercentage: 1.8 }];

// const plusDealsData = [
//   { rangeLower: 0, rangeUpper: 3000000, commissionPercentage: 2.0 }, // 0 - 30L
//   { rangeLower: 3000000, rangeUpper: 5000000, commissionPercentage: 2.05 }, // >30L - 50L
//   { rangeLower: 5000000, rangeUpper: 7500000, commissionPercentage: 2.1 }, // >50L - 75L
//   { rangeLower: 7500000, rangeUpper: 12500000, commissionPercentage: 2.15 }, // >75L - 1.25 Cr
//   { rangeLower: 12500000, rangeUpper: 25000000, commissionPercentage: 2.2 }, // >1.25 Cr - 2.5 Cr
//   { rangeLower: 25000000, rangeUpper: Infinity, commissionPercentage: 2.25 }, // More than 2.5 Cr
// ];

const CommissionTable: React.FC<CommissionTableProps> = () => {
  const { data, isLoading } = useGetPartnerProfile();
  const { invoiceDiscountingTerms } = data?.data || {};
  const { profileData } = useProfile();
  const renderCommissionTable = (title: string, data: typeof regularDealsData) => (
    <div>
      <h2 className="text-xl lg:text-2xl font-semibold text-teal-950 pt-6 ml-2">{title}</h2>
      <div className="mt-6 rounded-xl border border-stone-300">
        <div className="grid grid-cols-2 bg-black/[0.02] divide-x divide-stone-300">
          <div className="p-6">
            <p className="font-semibold text-gray-600 text-base lg:text-xl text-center justify-center items-center">
              Range
            </p>
          </div>
          <div className="p-6">
            <p className="font-semibold text-gray-600 text-base lg:text-xl text-center justify-center items-center ">
              Commission
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 w-full divide-x divide-y divide-stone-200">
          {data.map((term, index) => (
            <React.Fragment key={index}>
              {term.rangeUpper === Infinity ? (
                <div className="p-6 w-full justify-center items-center font-medium text-sm md:text-base lg:text-lg flex !border-l-0">
                  <span className="text-center text-gray-900">More than 2.5 Cr</span>
                </div>
              ) : term.rangeLower === 0 ? (
                <div className="py-6 px-28 w-full justify-center items-center font-medium text-sm md:text-base lg:text-lg flex !border-l-0 !border-t text-gray-900">
                  <span className="max-md:text-center text-center">Anything</span>
                </div>
              ) : (
                <div
                  className={`py-6 px-20 w-full justify-between items-center font-medium text-sm md:text-base lg:text-lg flex !border-l-0 text-gray-900 ${
                    index === 0 ? 'border-t ' : ''
                  }`}
                >
                  <span className="max-md:text-start text-center">
                    <span className="mr-1">{index > 0 && '>'}</span>
                    {term.rangeLower >= 10000000
                      ? `${twoDecimalNumber(term.rangeLower / 10000000)} Cr`
                      : `${twoDecimalNumber(term.rangeLower / 100000)} L`}
                  </span>
                  <span className="max-md:text-start text-center">-</span>
                  <span className="max-md:text-start text-center">
                    {term.rangeUpper >= 10000000
                      ? `${twoDecimalNumber(term.rangeUpper / 10000000)} Cr`
                      : `${twoDecimalNumber(term.rangeUpper / 100000)} L`}
                  </span>
                  <span className="ml-2 text-gray-900">
                    (
                    {term.rangeUpper - term.rangeLower >= 10000000
                      ? `${twoDecimalNumber(
                          (term.rangeUpper - term.rangeLower) / 10000000,
                        )} Cr Range`
                      : `${twoDecimalNumber((term.rangeUpper - term.rangeLower) / 100000)} L Range`}
                    )
                  </span>
                </div>
              )}
              <div className="p-6 font-medium text-sm md:text-base lg:text-lg text-center justify-center items-center text-gray-900">
                {twoDecimalNumber(term.commissionPercentage)}%
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="my-6 max-w-6xl mx-auto">
      {/* {profileData?.clientActions?.enableMarketingBanner && (
        <div className="my-6 flex items-center max-w-7xl mx-auto">
          <Image
            // @ts-ignorey
            src={WebinarbannerDesktop}
            alt=" Webinar link"
            className="rounded-xl"
         
          />
        </div>
      )} */}
      <div className="flex items-center gap-4 mb-4">
        <ArrowBack
          className="p-2 bg-green-700 bg-opacity-10 text-green-700 rounded-full"
          style={{ width: '32px', height: '32px' }}
          onClick={() => window.history.back()}
        />
        <h1 className="text-xl lg:text-2xl font-semibold text-teal-950">Commission Table</h1>
      </div>

      {renderCommissionTable('Invoice Discounting', regularDealsData)}
      <div className="my-6"></div>
      {/* {renderCommissionTable("Invoice Discounting- Plus Deals", plusDealsData)} */}

      <div className="flex flex-col mt-8 bg-black/[0.02]">
        <div className="border border-stone-300 rounded-xl">
          <h2 className=" text-center p-6 lg:text-xl font-semibold text-xl text-gray-600 mb-4">
            Invoice Discounting Deal Investment Commission Calculation
          </h2>
          <div className="px-6 pb-6">
            <p className="text-gray-900 font-medium text-lg mb-10 underline underline-offset-2 text-center">
              Commission per investment = % (1.8 / Downsell IRR Difference) × invested amount x
              tenure / 365
            </p>

            <div className="space-y-8">
              {/* Example 1 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Example 1:</h3>
                  <p className="font-medium text-lg text-green-700 mb-2">Regular Deal</p>
                </div>
                <div className="flex flex-col gap-6">
                  <p className="text-gray-900 text-sm">
                    Investment of 2 Lakh on regular deal for 62 days.
                  </p>
                  {/* <p className="text-gray-900 text-sm">
                    - Considering Partner's total investment for Invoices discounting on monthly
                    basis is assumed as 31 lakhs for calculations:
                  </p>
                  <p className="text-gray-900 text-sm">
                    With the above assumptions the commission for the investment is calculated as
                    follows:
                  </p> */}
                  <p className="font-medium text-gray-900 text-sm">
                    <span className="font-semibold">Commission</span> = ₹ 2 Lakh x 1.80% x (62 /
                    365) = <span className="font-semibold">₹ 611.51</span>
                  </p>
                  <div className="text-slate-500 flex flex-col gap-2">
                    {/* <p className="text-xs">
                      *Since the total invested amount for the partner for the particular month
                      falls under 30L - &lt; 50L range. Refer to the above regular deal slab table.
                    </p> */}
                    <p className="text-xs">
                      *The IRR/Deal Yield (%) is annualised, since deal is for 62 days, we divide it
                      with 365 days.
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="w-full my-8"
                style={{
                  height: '1px',
                  backgroundImage: 'linear-gradient(to right, #CBD5E1 50%, transparent 50%)',
                  backgroundSize: '15px 1px',
                  backgroundRepeat: 'repeat-x',
                }}
              ></div>
              {/* Example 2 */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Example 2:</h3>
                  <p className="font-medium text-lg text-green-700 mb-2">Downsell Deal</p>
                </div>
                <div className="flex flex-col gap-6">
                  <p className="text-gray-900 text-sm">
                    Investment of 2 Lakh in downsell deal for 62 days at 10% IRR with actual IRR as
                    12%.
                  </p>
                  {/* <p className="text-gray-900 text-sm">
                    - Considering Partner's total investment for Invoices discounting on monthly
                    basis is assumed as 31 lakhs for calculations:
                  </p> */}
                  <p className="text-gray-900 text-sm">
                    With the above assumptions the commission for the investment is calculated as
                    follows:
                  </p>
                  <p className="font-medium text-gray-900 text-sm">
                    <span className="font-semibold">Commission</span> = ₹ 2 Lakh x (12-10) % x (62 /
                    365) = <span className="font-semibold">₹ 679.45</span>
                  </p>
                  <div className="text-slate-500 flex flex-col gap-2">
                    {/* <p className="text-xs">
                      *Since the total invested amount for the partner for the particular month
                      falls under 30 L - &lt; 50 L range. Refer to the above plus deal slab table.
                    </p>
                    <p className="text-xs">
                      *The IRR % is annualised, since deal is for 62 days, we divide it with 365
                      days.
                    </p> */}
                    <p className="text-xs text-slate-500 ">
                      **Terms and conditions are subjected to change at any point in time at
                      company's discretion
                    </p>
                    <br />
                    <p className="text-xs text-slate-500 font-bold">
                      Note: Commissions are including GST of invested amount.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="border border-t-0 border-stone-300 mt-[-8px] rounded-b-xl">
          <Accordion
            elevation={0}
            defaultExpanded={false}
            sx={{
              '&:before': {
                display: 'none',
              },
              '& .MuiAccordionSummary-root': {
                minHeight: 'unset',
                padding: '24px',
              },
              '& .MuiAccordionDetails-root': {
                padding: '0 24px 24px 24px',
              },
              backgroundColor: 'transparent',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                // borderBottom: '1px solid #E5E7EB',
                borderRadius: '0.75rem',
                paddingBottom: 0,
                '& .MuiAccordionSummary-content': {
                  margin: '0',
                },
              }}
            >
              <h2 className="text-xl font-semibold text-slate-800">Frequently Asked Questions</h2>
            </AccordionSummary>
            <AccordionDetails>
              <div className="space-y-6 mt-4">
                <div>
                  <h3 className="font-medium text-slate-800 mb-2">
                    Are the commission based on individual investments or clients?
                  </h3>
                  <p className="text-slate-600">
                    The commission are based on individual investments. Each investment's commission
                    is calculated based on the total AUM at the end of the month in which the
                    investment was made.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-slate-800 mb-2">
                    If I invest in Regular Deals/ Downsell Deals will my commission come out of user
                    yield?
                  </h3>
                  <p className="text-slate-600">
                    No, your commission is paid by Tap Invest and does not affect the client's yield
                    in any way.
                  </p>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 border border-stone-300 my-6 rounded-xl">
        <div className="bg-black/[0.02] p-6 items-center justify-center flex">
          <p className="text-base lg:text-xl font-bold text-gray-900">Asset Leasing</p>
        </div>
        <div className="col-span-2 p-6 text-center text-sm md:text-base lg:text-lg font-medium">
          <p>
            0.5% across all Asset leasing investments (Commissions are including GST of invested
            amount)
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 border border-stone-300 my-6 rounded-xl">
        <div className="bg-black/[0.02] p-6  flex justify-center items-center">
          <p className="text-base lg:text-xl font-bold text-slate-700">Digital Gold</p>
        </div>
        <div className="col-span-2 p-6 text-center text-sm md:text-base lg:text-lg font-medium">
          {/* <p>0.5% across all gold investments(Commissions are excluding GST of invested amount)</p> */}
          <p className="text-center">
            0.5% across all gold investments (Commissions are including GST of invested amount)
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 border border-stone-300 my-6 rounded-xl">
        <div className="bg-black/[0.02] p-6 items-center justify-center flex">
          <p className="text-base lg:text-xl font-bold text-gray-900">Fixed Deposit</p>
        </div>
        <div className="col-span-2 p-6 text-center text-sm md:text-base lg:text-lg font-medium">
          <p>
            0.225% Annualised across all FD investments (Commissions are including GST of invested
            amount).
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 border border-stone-300 my-6 rounded-xl">
        <div className="bg-black/[0.02] p-6  flex justify-center items-center">
          <p className="text-base lg:text-xl font-bold text-slate-700">Pre IPO</p>
        </div>
        <div className="col-span-2 p-6 text-center text-sm md:text-base lg:text-lg font-medium">
          <p className="text-center">
            In our Pre-IPO program, partners earn through a simple model: we share the daily landing
            price (your cost), and you’re free to sell at any price. The margin between your selling
            price and the landing price is your earning (Commissions are including GST of invested
            amount).
          </p>
        </div>
      </div>
    </div>
  );
};

CommissionTable.displayName = 'CommissionTable';

export default CommissionTable;
